import { NsnCandidate, PartFinderReport } from '@/api/generatedApi';
import { NsnCandidateSummary } from '@/components/PartFinder/NsnCandidateSummary';
import { usePartFinderSearch } from '@/components/PartFinder/usePartFinderSearch';
import { useNsnCandidates } from '@/hooks/api/part-finder-reports/useNsnCandidates';
import { useToast } from '@/hooks/useToast';
import { AltRouteOutlined } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { CollapsibleSection } from './CollapsibleSection';

const CandidateRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  padding: '8px 0',
  borderTop: `1px solid ${theme.palette.divider}`,
}));

interface OtherNsnsListProperties {
  partNumber: string;
  currentNsn: string;
  candidates: NsnCandidate[];
}

const OtherNsnsList = ({ partNumber, currentNsn, candidates }: OtherNsnsListProperties) => {
  const { runSearch, isPending } = usePartFinderSearch();
  const { showToast } = useToast();

  const runReport = async (choice: { niin?: string; partNumberOnly?: boolean }) => {
    try {
      await runSearch({ partNumber, ...choice });
    } catch {
      showToast({ message: 'Could not start that report. Please try again.', type: 'error' });
    }
  };

  return (
    <CollapsibleSection icon={<AltRouteOutlined />} title='Other NSNs for this part number'>
      <Typography variant='body2' sx={{ marginBottom: '8px' }} data-testid='other-nsns-summary'>
        {partNumber} matches {candidates.length} NSNs in FLIS. This report uses {currentNsn}.
      </Typography>
      {candidates
        .filter(candidate => candidate.nsn !== currentNsn)
        .map(candidate => (
          <CandidateRow key={candidate.niin}>
            <NsnCandidateSummary candidate={candidate} />
            <Button
              size='small'
              variant='outlined'
              disabled={isPending}
              onClick={() => void runReport({ niin: candidate.niin })}
              sx={{ flexShrink: 0 }}
            >
              Run report
            </Button>
          </CandidateRow>
        ))}
      <CandidateRow>
        <Typography variant='body2'>Search as a part number only (skip FLIS)</Typography>
        <Button
          size='small'
          variant='outlined'
          disabled={isPending}
          onClick={() => void runReport({ partNumberOnly: true })}
          sx={{ flexShrink: 0 }}
        >
          Run report
        </Button>
      </CandidateRow>
    </CollapsibleSection>
  );
};

export const OtherNsnsSection = ({ report }: { report: PartFinderReport }) => {
  const currentNsn = report.nsnReport?.searchedNsn ?? report.nsnReport?.nsn;
  const { data } = useNsnCandidates(report.partNumber, Boolean(currentNsn));
  const candidates = data?.nsnCandidates ?? [];

  return (
    <>
      {currentNsn && candidates.length > 1 && (
        <OtherNsnsList partNumber={report.partNumber} currentNsn={currentNsn} candidates={candidates} />
      )}
    </>
  );
};
